// src/components/Dashboard.tsx
import { Card, CardContent, Typography, Grid, Box } from "@mui/material";
import { Home, BookOnline, People } from "@mui/icons-material";
import { useGetList } from "react-admin";
import { useState, useEffect } from "react";
import { apiUrl } from "../httpClient";
import { handleApiError } from "../utils/errorHandler";
import { formatNumber } from "../utils/formatters";
import type { PropertyStats } from "../types/api";

const StatCard = ({
  title,
  value,
  icon,
  color = "primary",
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
}) => (
  <Card>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="textSecondary" gutterBottom variant="h6">
            {title}
          </Typography>
          <Typography variant="h4" component="div">
            {value}
          </Typography>
        </Box>
        <Box color={`${color}.main`}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

export const Dashboard = () => {
  const { total: totalUsers } = useGetList("users", {
    pagination: { page: 1, perPage: 1 },
  });

  const { total: totalBookings } = useGetList("bookings", {
    pagination: { page: 1, perPage: 1 },
  });

  const [propertyStats, setPropertyStats] = useState<PropertyStats | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPropertyStats = async () => {
      try {
        const response = await fetch(apiUrl("/properties/stats/overview"), {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
          },
        });

        if (response.ok) {
          const stats = await response.json();
          setPropertyStats(stats);
        }
      } catch (error) {
        const appError = handleApiError(error);
        console.error("Failed to fetch property stats:", appError.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyStats();
  }, []);

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Welcome to TripNester Admin
      </Typography>
      <Typography variant="body1" color="textSecondary" gutterBottom>
        Manage your property booking platform
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={formatNumber(totalUsers)}
            icon={<People sx={{ fontSize: 40 }} />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Properties"
            value={
              loading ? "..." : formatNumber(propertyStats?.totalProperties)
            }
            icon={<Home sx={{ fontSize: 40 }} />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Properties"
            value={
              loading ? "..." : formatNumber(propertyStats?.activeProperties)
            }
            icon={<Home sx={{ fontSize: 40 }} />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Bookings"
            value={formatNumber(totalBookings)}
            icon={<BookOnline sx={{ fontSize: 40 }} />}
            color="success"
          />
        </Grid>
      </Grid>
    </Box>
  );
};
